
import { useState, useEffect, useCallback } from "react";

type ClientEdit = {
  content: string;
  editedBy: 'client' | 'owner';
  timestamp: number;
};

type UseNodeEditingProps = {
  id: string;
  updateNodeData: (id: string, data: any) => void;
  isResizing: boolean;
  isClientMode?: boolean;
  originalContent?: string;
  clientEdits?: ClientEdit[];
};

export const useNodeEditing = ({ 
  id, 
  updateNodeData,
  isResizing,
  isClientMode = false,
  originalContent,
  clientEdits = []
}: UseNodeEditingProps) => {
  const [isEditingContent, setIsEditingContent] = useState(false);
  
  const handleContentClick = useCallback(() => {
    console.log("Content clicked for node:", id, "isResizing:", isResizing);
    
    if (!isResizing) {
      console.log("Starting edit mode for node:", id);
      setIsEditingContent(true);
    } else {
      console.log("Cannot edit - node is resizing");
    }
  }, [id, isResizing]);

  const handleContentChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    console.log("Content changing for node:", id, "new value:", newValue);
    
    // Validate updateNodeData function
    if (typeof updateNodeData !== 'function') {
      console.error("updateNodeData function is missing for node:", id);
      console.error("updateNodeData value:", updateNodeData);
      return;
    }
    
    try {
      updateNodeData(id, { 
        label: newValue,
        lastEdited: Date.now(),
        editedBy: isClientMode ? 'client' : 'owner'
      });
      
      console.log("Content update successful for node:", id, "new label:", newValue);
    } catch (error) {
      console.error("Error updating content for node:", id, error);
    }
  }, [id, updateNodeData, isClientMode]);

  const handleContentBlur = useCallback(() => {
    console.log("Content blur for node:", id, "ending edit mode");
    setIsEditingContent(false);
  }, [id]);

  // Close editing when resizing starts
  useEffect(() => {
    if (isResizing && isEditingContent) {
      console.log("Closing edit mode due to resize for node:", id);
      setIsEditingContent(false);
    }
  }, [isResizing, isEditingContent, id]);

  // Debug log when updateNodeData changes
  useEffect(() => {
    console.log("useNodeEditing updateNodeData changed for node:", id, "is function:", typeof updateNodeData === 'function');
  }, [updateNodeData, id]);

  return {
    isEditingContent,
    handleContentClick,
    handleContentChange,
    handleContentBlur
  };
};
